import type * as RMDX from '@readme/markdown';
import type { Node } from 'slate';

import { Editor, Text } from 'slate';

import { ImageBlock, Figure, Paragraph } from '@ui/MarkdownEditor/editor/blocks';
import { blocksByType as blocks } from '@ui/MarkdownEditor/editor/byType';
import type { FigureElement } from '@ui/MarkdownEditor/types';

import HorizontalRule from '../blocks/HorizontalRule';

export const slateToMdast = (node: Node, { parent, renderingLibrary }) => {
  const deeper = n => n.children.flatMap(child => slateToMdast(child, { parent: n, renderingLibrary }));

  if (Text.isText(node)) {
    return { type: 'plain', value: node.text };
  } else if (Editor.isEditor(node)) {
    return {
      type: 'root',
      children: deeper(node),
    };
  }

  switch (node.type) {
    case 'heading': {
      const children = node.children.map((child, idx) =>
        // @ts-expect-error -- @todo
        idx === 0 ? { text: child.text.replace(/^#+ ?/, '') } : child,
      );

      return {
        ...node,
        children: deeper({ children }),
      };
    }
    case HorizontalRule.type:
      return {
        type: 'thematicBreak',
      };
    case Figure.type: {
      return ImageBlock.serialize(node as FigureElement, deeper, {
        renderingLibrary,
        parent,
      });
    }
    default: {
      const block = blocks[node.type];
      const mdNode = block?.serialize
        ? block.serialize(node, deeper, { renderingLibrary, parent })
        : {
            ...node,
            children: deeper(node),
            type: block?.rdmdType || node.type,
          };

      return mdNode;
    }
  }
};

const isEmptyEditor = (node: Node) => {
  const [firstChild] = (('children' in node && node.children) || []) satisfies Node[];

  return (
    Editor.isEditor(node) &&
    node.children.length === 1 &&
    Paragraph.isParagraph(firstChild) &&
    firstChild.children.length === 1 &&
    Text.isText(firstChild.children[0]) &&
    firstChild.children[0].text === ''
  );
};

const serializeMdx = (
  node: Node,
  { parent, renderingLibrary }: { parent: Node | null; renderingLibrary: typeof RMDX },
) => {
  if (isEmptyEditor(node)) {
    // @note: this has the unfortunate side effect of converting the following
    // markdown to an empty string:
    //
    // ```
    // <br />
    // ```
    //
    // That is to say, if a customer had a page that was only a single br tag, we
    // would save it back out as an empty string. :eek:
    return '';
  }

  let mdast = slateToMdast(node, { parent, renderingLibrary });

  if (mdast.type !== 'root') {
    mdast = { type: 'root', children: Array.isArray(mdast) ? mdast : [mdast] };
  }

  const string = renderingLibrary.mdx(mdast);

  return string;
};

export default serializeMdx;
